import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import { toSessionOrg } from '@/features/auth/sessionOrg';
import type { OrganizationDto } from '@/features/auth/types';
import { useSession } from '@/stores/session';
import * as api from './api';

/** The session store keeps its own trimmed Organization (branding + deal
 *  defaults). Mirror a saved org into it, or the app reads stale settings until
 *  the next bootstrap. */
function syncSessionOrg(id: number, org: OrganizationDto) {
  useSession.getState().patchOrganization(id, toSessionOrg(org));
}

export const orgKeys = {
  all: ['organization'] as const,
  detail: (id: number) => [...orgKeys.all, 'detail', id] as const,
};

export const auditKeys = {
  all: ['audit-logs'] as const,
  list: (p: api.AuditLogParams) => [...auditKeys.all, 'list', p] as const,
};

export function useAuditLogs(params: api.AuditLogParams = {}) {
  return useQuery({
    queryKey: auditKeys.list(params),
    queryFn: () => api.listAuditLogs(params),
    placeholderData: keepPreviousData,
  });
}

export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['integration-status'],
    queryFn: () => api.getIntegrationStatus(),
  });
}

export function useOrganization(id: number) {
  return useQuery({
    queryKey: orgKeys.detail(id),
    queryFn: () => api.getOrganization(id),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useUpdateOrganization(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<api.OrganizationUpdateInput>) =>
      api.updateOrganization(id, input),
    onSuccess: (org) => {
      qc.setQueryData(orgKeys.detail(id), org);
      syncSessionOrg(id, org);
      // Confirmed in place by the settings form's SaveButton (FE-ADR-065).
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message); // field errors map onto the form
    },
  });
}

export function useUploadOrgLogo(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadOrganizationLogo(id, file),
    onSuccess: (org) => {
      qc.setQueryData(orgKeys.detail(id), org);
      syncSessionOrg(id, org);
      toast.success('Logo uploaded');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Company bank accounts ----
export const bankAccountKeys = {
  all: ['company-bank-accounts'] as const,
  list: () => [...bankAccountKeys.all, 'list'] as const,
};

export function useCompanyBankAccounts() {
  return useQuery({
    queryKey: bankAccountKeys.list(),
    queryFn: () => api.listCompanyBankAccounts(),
  });
}

export function useCreateCompanyBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: api.CompanyBankAccountInput) => api.createCompanyBankAccount(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountKeys.all });
      toast.success('Bank account added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateCompanyBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: api.CompanyBankAccountInput }) =>
      api.updateCompanyBankAccount(publicId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountKeys.all });
      toast.success('Bank account updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteCompanyBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteCompanyBankAccount(publicId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bankAccountKeys.all });
      toast.success('Bank account deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Service bots & API keys (ADR-081) ----
export const botKeys = {
  all: ['org-bots'] as const,
  list: () => [...botKeys.all, 'list'] as const,
};

export function useBots(enabled = true) {
  return useQuery({
    queryKey: botKeys.list(),
    queryFn: () => api.listBots(),
    enabled,
  });
}

export function useCreateBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; role_ids?: number[]; user_id?: number }) =>
      api.createBot(input),
    onSuccess: (bot) => {
      qc.invalidateQueries({ queryKey: botKeys.all });
      toast.success(bot.kind === 'user' ? 'Token created' : 'Bot created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useSetBotRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prefix, roleIds }: { prefix: string; roleIds: number[] }) =>
      api.setBotRoles(prefix, roleIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: botKeys.all });
      toast.success('Roles updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRotateBotKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefix: string) => api.rotateBotKey(prefix),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: botKeys.all });
      toast.success('Key rotated — the old key no longer works');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRevokeBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefix: string) => api.revokeBot(prefix),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: botKeys.all });
      toast.success('Bot revoked');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
